import { Inject, inject, Injectable, InjectionToken, OnDestroy } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { SharedLanguageService } from '@language';
import { BehaviorSubject, combineLatest, Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { PublicSettingsDataService } from '../data-services/public-settings.data-service';
export const PAGE_TITLE_CONFIG = new InjectionToken<PageTitleConfig>('PageTitleConfig');

interface PageTitleConfig {
    usePageHeaderSuffix: boolean;
}
@Injectable({
    providedIn: 'root'
})
export class PageTitleService implements OnDestroy {
    private observer?: MutationObserver;
    private readonly titleService: Title = inject(Title);

    private readonly publicSettingsDataService: PublicSettingsDataService = inject(PublicSettingsDataService);
    private readonly translateService = inject(SharedLanguageService);

    private prefix$: Observable<string>;
    private suffix$: BehaviorSubject<string>;

    /**
     * @param usePageHeaderSuffix When set, will try to detect page header and use as suffix
     */
    constructor(@Inject(PAGE_TITLE_CONFIG) private readonly config: PageTitleConfig) {
        this.initPrefix();
        this.initSuffix();
        this.initTitle();
    }

    ngOnDestroy() {
        this.observer?.disconnect();
    }

    // #region Prefix
    private initPrefix() {
        this.prefix$ = this.getTitlePrefix();
    }

    private getTitlePrefix(): Observable<string> {
        const prefixFallback = this.translateService.instant('app');

        return this.publicSettingsDataService.getWebsiteSetting('site_name').pipe(
            catchError(() => of(prefixFallback)),
            map(siteName => (siteName !== '' ? siteName : prefixFallback))
        );
    }
    // #endregion

    // #region Suffix
    private initSuffix() {
        const suffixFallback = this.translateService.instant('app');
        this.suffix$ = new BehaviorSubject<string>(suffixFallback);

        if (this.config.usePageHeaderSuffix) {
            this.initPageTitleListener(suffixFallback);
        }
    }

    private initPageTitleListener(suffixFallback: string): void {
        if (!this.config.usePageHeaderSuffix) {
            return;
        }

        this.observer = new MutationObserver(() => {
            const pageTitleEl = document.querySelector('.sh-page-title');

            const pageTitle = pageTitleEl?.textContent?.trim() ?? '';

            if (!pageTitle) {
                this.suffix$.next(suffixFallback);
            } else if (this.suffix$.value !== pageTitle) {
                this.suffix$.next(pageTitle);
            }
        });

        this.observer.observe(document.body, {
            subtree: true,
            characterData: true
        });
    }
    // #endregion

    private initTitle() {
        combineLatest([this.prefix$, this.suffix$]).subscribe(([prefix, suffix]) => {
            // Happens when no site name is configured and usePageHeaderSuffix is false
            if (prefix === suffix) {
                this.titleService.setTitle(prefix);
            } else {
                this.titleService.setTitle(`${prefix} - ${suffix}`);
            }
        });
    }
}
